import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../../lib/api";
import PublicHeader from "../../components/layout/PublicHeader";
import PublicFooter from "../../components/layout/PublicFooter";
import CheckoutPanel from "../../components/CheckoutPanel";
import SubscriptionPanel from "../../components/SubscriptionPanel";
import { formatCLP, formatUSD, AVX_FORMATS } from "../../lib/format";

export default function BundleDetail() {
    const { slug } = useParams();
    const [bundle, setBundle] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        api.get(`/bundles/${slug}`)
            .then(({ data }) => setBundle(data))
            .catch(() => setNotFound(true));
    }, [slug]);

    if (notFound) {
        return (
            <div className="min-h-screen flex flex-col">
                <PublicHeader />
                <main className="flex-1 max-w-3xl mx-auto px-6 py-24 text-center">
                    <h1 className="font-heading font-black text-3xl mb-3">Bundle not available</h1>
                    <p className="text-avx-muted mb-6">The bundle you are looking for does not exist or was removed.</p>
                    <Link to="/plugins" className="btn-primary px-5 py-2.5 text-xs font-mono uppercase tracking-[0.2em] rounded-sm inline-block">Back to catalog</Link>
                </main>
                <PublicFooter />
            </div>
        );
    }
    if (!bundle) {
        return (
            <div className="min-h-screen flex flex-col">
                <PublicHeader />
                <main className="flex-1 max-w-3xl mx-auto px-6 py-24 text-center text-avx-muted">Loading…</main>
                <PublicFooter />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col" data-testid="bundle-detail-page">
            <PublicHeader />
            <section className="border-b border-avx-line bg-avx-glow">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
                    <div className="flex items-center gap-3 mb-5 flex-wrap">
                        <Link to="/plugins" className="label-mono text-avx-muted hover:text-avx-blue">← Catalog</Link>
                        <span className="text-avx-muted">·</span>
                        <span className="label-mono text-avx-blue">/ Bundle</span>
                        {bundle.badge_text && (
                            <span className="px-2 py-1 rounded-sm bg-avx-blue/15 border border-avx-blue/40 text-avx-blue text-[10px] font-mono uppercase tracking-[0.2em]">
                                {bundle.badge_text}
                            </span>
                        )}
                    </div>
                    <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-4 leading-[1.05]">
                        {bundle.name}
                    </h1>
                    <p className="text-lg text-avx-muted max-w-2xl leading-relaxed">{bundle.short_description}</p>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid lg:grid-cols-[1.6fr_1fr] gap-10 w-full flex-1">
                <div>
                    {bundle.full_description && (
                        <div className="panel p-6 mb-8" data-testid="bundle-full-description">
                            <div className="label-mono mb-2">/ About this bundle</div>
                            <p className="text-base text-avx-text/90 leading-relaxed whitespace-pre-line">{bundle.full_description}</p>
                        </div>
                    )}

                    <div className="label-mono mb-4">/ Included plugins ({bundle.products?.length || 0})</div>
                    <div className="grid sm:grid-cols-2 gap-4" data-testid="bundle-products-grid">
                        {(bundle.products || []).map((p) => (
                            <Link to={`/plugins/${p.slug}`} key={p.id} className="panel p-5 hover:border-avx-blue/50 transition-colors group">
                                <h3 className="font-heading font-bold text-lg mb-1 group-hover:text-avx-blue transition-colors">{p.name}</h3>
                                <div className="label-mono mb-2">v{p.current_version} · {AVX_FORMATS.join(" · ")}</div>
                                <p className="text-xs text-avx-muted leading-relaxed line-clamp-3">{p.short_description}</p>
                                <div className="text-[10px] font-mono text-avx-muted mt-3">
                                    Individual price: <span className="text-avx-text/80">{formatCLP(p.price_clp)}</span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>

                <div className="space-y-5">
                    {bundle.sale_mode === "subscription" ? (
                        <SubscriptionPanel bundle={bundle} />
                    ) : (
                        <>
                            <div className="panel-elevated p-6" data-testid="bundle-pricing-summary">
                                <div className="label-mono mb-4 text-avx-blue">/ Price summary</div>
                                <div className="space-y-2 text-sm font-mono">
                                    <div className="flex justify-between text-avx-muted">
                                        <span>Sum of individual prices</span>
                                        <span className="line-through">{formatCLP(bundle.original_price_clp)}</span>
                                    </div>
                                    <div className="flex justify-between text-avx-text">
                                        <span>Bundle price</span>
                                        <span className="text-avx-blue font-bold text-base">{formatCLP(bundle.price_clp)}</span>
                                    </div>
                                    {bundle.savings_clp > 0 && (
                                        <div className="flex justify-between text-avx-success pt-2 border-t border-avx-line">
                                            <span>Your savings</span>
                                            <span className="font-bold">{formatCLP(bundle.savings_clp)} · −{bundle.discount_pct}%</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <CheckoutPanel bundle={bundle} />
                        </>
                    )}
                </div>
            </section>

            <PublicFooter />
        </div>
    );
}
